import React from 'react';
import { Sparkles, BookOpen, Feather, TextSelect, Award } from 'lucide-react';

// Reading Milestones — a visual progression that reassures parents about
// realistic reading development without making guarantees.

const milestones = [
  {
    stage: 'Beginning Reader',
    description: 'Recognises and produces letter sounds.',
    icon: Sparkles,
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  {
    stage: 'Emerging Reader',
    description: 'Blends simple CVC words independently.',
    icon: BookOpen,
    color: 'bg-teal-50 text-teal-700 border-teal-200'
  },
  {
    stage: 'Developing Reader',
    description: 'Reads blends, digraphs and common words.',
    icon: Feather,
    color: 'bg-sky-50 text-sky-700 border-sky-200'
  },
  {
    stage: 'Sentence Reader',
    description: 'Reads and understands simple sentences.',
    icon: TextSelect,
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  },
  {
    stage: 'Confident Early Reader',
    description:
      'Reads beginner books with increasing fluency and independence.',
    icon: Award,
    color: 'bg-amber-50 text-amber-700 border-amber-200'
  }
];

const ReadingMilestones = () => {
  return (
    <section
      id="reading-milestones"
      data-testid="reading-milestones"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-block bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Reading Milestones
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            What Will My Child Learn?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            A gentle progression of reading development, from first sounds to
            confident early reading.
          </p>
        </div>

        {/* Vertical timeline */}
        <div className="relative">
          <div
            aria-hidden="true"
            className="absolute left-6 sm:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-emerald-200 via-sky-200 to-amber-200"
          />
          <ol className="space-y-6">
            {milestones.map((m, idx) => {
              const Icon = m.icon;
              return (
                <li
                  key={idx}
                  data-testid={`milestone-${idx}`}
                  className="relative flex items-start gap-4 sm:gap-6"
                >
                  <div
                    className={`relative z-10 w-12 h-12 sm:w-16 sm:h-16 rounded-2xl border-2 ${m.color} flex items-center justify-center flex-shrink-0 shadow-sm`}
                  >
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </div>
                  <div className="flex-1 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 p-5 sm:p-6">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Stage {idx + 1}
                    </p>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                      {m.stage}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {m.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="mt-10 rounded-2xl bg-slate-50 border border-slate-100 p-5 text-center max-w-3xl mx-auto">
          <p className="text-sm sm:text-base text-gray-600 italic leading-relaxed">
            Children progress at their own pace. These milestones are used as
            learning guides rather than rigid targets.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReadingMilestones;
